/**
 * receiptHtml.ts
 * Generates an HTML receipt for system printing via window.print().
 * Works on: Windows (all installed printers), Android (Android Print system),
 * and any OS that supports browser printing.
 */
import { BillData } from '../services/BluetoothPrinterService';

export function generateReceiptHTML(bill: BillData): string {
  const methodIcon: Record<string, string> = { Cash: '💵', Card: '💳', UPI: '📱' };
  const icon = methodIcon[bill.paymentMethod ?? ''] ?? '💰';

  const itemRows = bill.items
    .map(
      (item) => `
    <tr>
      <td class="item-name">${escHtml(item.name)}${item.specialInstructions ? `<div class="item-note">★ ${escHtml(item.specialInstructions)}</div>` : ''}</td>
      <td class="center">${item.quantity}</td>
      <td class="right">₹${item.unitPrice.toFixed(2)}</td>
      <td class="right amt">₹${item.totalPrice.toFixed(2)}</td>
    </tr>`,
    )
    .join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Receipt – ${escHtml(bill.billNumber)}</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Courier New',Courier,monospace;font-size:13px;background:#fff;color:#111;
       max-width:82mm;margin:0 auto;padding:4mm 3mm}
  .logo{text-align:center;margin-bottom:2mm}
  .r-name{font-size:18px;font-weight:900;letter-spacing:1px;text-transform:uppercase}
  .r-sub{font-size:11px;color:#444;margin-top:1px}
  .sep-solid{border:none;border-top:2px solid #111;margin:3mm 0}
  .sep-dash {border:none;border-top:1px dashed #555;margin:2.5mm 0}
  table{width:100%;border-collapse:collapse}
  .info-tbl td{padding:1px 0;vertical-align:top}
  .info-tbl td:last-child{text-align:right;white-space:nowrap;padding-left:4px}
  thead th{font-size:11px;text-transform:uppercase;border-bottom:1px solid #111;padding:2px 0}
  tbody td{padding:3px 0;vertical-align:top;font-size:12px}
  .item-name{max-width:38mm;word-break:break-word}
  .center{text-align:center}
  .right{text-align:right}
  .amt{white-space:nowrap}
  .total-row td{font-weight:900;font-size:16px;padding-top:3px}
  .discount{color:#1a7a1a}
  .item-note{font-size:10px;color:#666;font-style:italic;margin-top:1px}
  .order-note{font-size:11px;color:#444;font-style:italic;padding:2mm 0}
  .footer{text-align:center;margin-top:4mm;font-size:11px;color:#444;line-height:1.6}
  .footer strong{font-size:13px;color:#111}
  .powered{font-size:10px;color:#aaa;margin-top:1mm}
  @media print{
    body{max-width:100%}
    @page{margin:3mm;size:80mm auto}
  }
</style>
</head>
<body>
<!-- Header -->
<div class="logo">
  <div class="r-name">${escHtml(bill.restaurantName)}</div>
  ${bill.restaurantAddress ? `<div class="r-sub">${escHtml(bill.restaurantAddress)}</div>` : ''}
  ${bill.restaurantPhone ? `<div class="r-sub">📞 ${escHtml(bill.restaurantPhone)}</div>` : ''}
</div>
<hr class="sep-solid">

<!-- Bill meta -->
<table class="info-tbl">
  <tr><td>Bill No</td><td><b>${escHtml(bill.billNumber)}</b></td></tr>
  <tr><td>Date</td><td>${escHtml(bill.date)}</td></tr>
  ${bill.tableNumber ? `<tr><td>Table</td><td>T-${escHtml(bill.tableNumber)}</td></tr>` : ''}
  ${bill.customerName ? `<tr><td>Customer</td><td>${escHtml(bill.customerName)}</td></tr>` : ''}
  ${bill.customerPhone ? `<tr><td>Phone</td><td>${escHtml(bill.customerPhone)}</td></tr>` : ''}
</table>
<hr class="sep-dash">

<!-- Items -->
<table>
  <thead>
    <tr>
      <th>Item</th>
      <th class="center">Qty</th>
      <th class="right">Rate</th>
      <th class="right">Amt</th>
    </tr>
  </thead>
  <tbody>${itemRows}</tbody>
</table>
${bill.notes ? `<hr class="sep-dash"><div class="order-note">📝 ${escHtml(bill.notes)}</div>` : ''}
<hr class="sep-dash">

<!-- Subtotals -->
<table class="info-tbl">
  <tr><td>Subtotal</td><td>₹${bill.subtotal.toFixed(2)}</td></tr>
  <tr><td>GST (18%)</td><td>₹${bill.tax.toFixed(2)}</td></tr>
  ${
    bill.discount > 0
      ? `<tr class="discount"><td>Discount${bill.discountLabel ? ` (${escHtml(bill.discountLabel)})` : ''}</td>
           <td>-₹${bill.discount.toFixed(2)}</td></tr>`
      : ''
  }
</table>
<hr class="sep-solid">

<!-- Grand total -->
<table class="info-tbl">
  <tr class="total-row"><td>TOTAL</td><td>₹${bill.total.toFixed(0)}</td></tr>
</table>
<hr class="sep-dash">

<!-- Payment -->
<table class="info-tbl">
  ${bill.paymentMethod ? `<tr><td>Payment</td><td>${icon} ${escHtml(bill.paymentMethod)}</td></tr>` : ''}
  ${bill.tenderedAmount !== undefined && bill.paymentMethod === 'Cash'
    ? `<tr><td>Tendered</td><td>₹${bill.tenderedAmount.toFixed(0)}</td></tr>
       <tr><td>Change</td><td>₹${(bill.changeAmount ?? 0).toFixed(0)}</td></tr>`
    : ''}
</table>

<!-- Footer -->
<div class="footer">
  <hr class="sep-dash">
  <strong>${escHtml(bill.footerNote ?? 'Thank you! Visit again! 🙏')}</strong>
  <div class="powered">Powered by MenuMaster POS</div>
</div>
</body>
</html>`;
}

function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Print via the OS/Android system print dialog.
 * Opens a popup with the formatted receipt HTML, triggers window.print().
 * Works on: Windows (all installed printers), Android (Android Print system + BT printers).
 */
export function printViaSystemDialog(bill: BillData): boolean {
  const html = generateReceiptHTML(bill);
  const pw = window.open('', '_blank', 'width=420,height=680,scrollbars=yes');
  if (!pw) return false; // blocked by popup blocker
  pw.document.write(html);
  pw.document.close();
  pw.focus();
  // Slight delay so the doc renders before print dialog opens
  setTimeout(() => {
    pw.print();
    // Don't auto-close — let user confirm/cancel first
    pw.onafterprint = () => pw.close();
  }, 350);
  return true;
}
